import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { PlexusCanvas } from "@/components/PlexusCanvas";
import { useSite } from "@/context/SiteContext";

export const Hero = () => {
    const { t } = useSite();
    const videoRef = useRef(null);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const tryPlay = () => v.play().catch(() => {});
        tryPlay();
        v.addEventListener("canplay", tryPlay);
        return () => v.removeEventListener("canplay", tryPlay);
    }, []);
    return (
        <section id="hero" data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-chitta-deep">
            <video
                ref={videoRef}
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/assets/hero-poster.jpg"
                role="img"
                aria-label={t.hero.alt}
            >
                <source src="/assets/hero-loop.webm" type="video/webm" />
                <source src="/assets/hero-loop.mp4" type="video/mp4" />
            </video>
            <div aria-hidden="true" className="absolute inset-0 bg-chitta-deep/50" />
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(2,11,29,0.94) 0%, rgba(7,48,119,0.42) 45%, rgba(2,11,29,0.45) 100%)" }}
            />
            <PlexusCanvas className="pointer-events-none absolute inset-0 h-full w-full opacity-70" />
            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 pb-24 pt-40 sm:px-8 sm:pb-28 lg:px-12">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mb-6 font-display text-sm font-medium uppercase tracking-[0.28em] text-chitta-sky sm:mb-8"
                >
                    {t.hero.kicker}
                </motion.p>
                <h1 className="headline-fluid font-display font-semibold text-white" data-testid="hero-headline">
                    <motion.span
                        className="block"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {t.hero.lead}
                    </motion.span>
                    <motion.span
                        className="text-gradient block"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {t.hero.accent}
                    </motion.span>
                    <motion.span
                        className="block"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {t.hero.line2}
                    </motion.span>
                </h1>
            </div>
            <div aria-hidden="true" className="absolute bottom-0 right-6 z-10 hidden h-28 w-px overflow-hidden bg-white/15 sm:right-10 md:block">
                <span className="block h-full w-px animate-scroll-cue bg-chitta-yellow" />
            </div>
        </section>
    );
};
